import type { PlausibilityMode } from "@sitescene/types";

import { useState } from "react";

import { Button } from "@/components/ui/buttons/button";
import { Input } from "@/components/ui/input/input";
import {
	type BackfillTotals,
	useBackfill,
} from "@/features/plausibility/use-backfill";
import { usePlausibilitySettings } from "@/features/plausibility/use-plausibility-settings";

/**
 * Rough model spend per verified clip (docs/plausibility-verification.md §9),
 * for an order-of-magnitude estimate on the preview only.
 */
const COST_PER_CLIP_USD = 0.03;

/**
 * What a run does to past events, from the saved settings: live mode applies
 * the verifier's decisions, and the footage check applies its own results in
 * any mode. With neither, results are only recorded.
 */
function pastEventEffect(
	mode: PlausibilityMode | undefined,
	claimsCheck: boolean,
): string {
	if (mode === ("live" as PlausibilityMode)) {
		return "This can change severity, titles, and descriptions on past events.";
	}
	if (claimsCheck) {
		return "The footage check is on, so its corrections, rule floors and lowered severities apply to past events: their severity, titles, and descriptions can change.";
	}
	return "Results are recorded only; past events are not changed.";
}

/**
 * Run plausibility verification over this organization's history: alert-level
 * measurements recorded before the verifier existed. Preview first; a run is
 * offered only for the range just previewed. Re-running is safe, since
 * anything already verified is skipped, so a preview after the queue drains
 * shows what is left.
 */
export const BackfillPanel = () => {
	const [from, setFrom] = useState("");
	const [to, setTo] = useState("");
	const [progress, setProgress] = useState<BackfillTotals | null>(null);
	const [previewedRange, setPreviewedRange] = useState<null | string>(null);
	const backfill = useBackfill(setProgress);
	const settings = usePlausibilitySettings().data;
	const effect = pastEventEffect(
		settings?.plausibilityMode,
		settings?.claimsCheckEnabled === true,
	);

	const range = `${from}|${to}`;
	const totals = backfill.isPending ? progress : (backfill.data ?? progress);
	const canRun = !backfill.isPending && previewedRange === range;

	const start = (dryRun: boolean) => {
		setProgress(null);
		backfill.mutate(
			{ dryRun, ...(from && { from }), ...(to && { to }) },
			{
				onSuccess: (result) =>
					setPreviewedRange(
						result.dryRun && result.candidates > 0 ? range : null,
					),
			},
		);
	};

	const run = () => {
		const count = backfill.data?.candidates ?? 0;
		if (
			window.confirm(
				`Verify ${count} measurements? ${effect} Customers are not notified.`,
			)
		) {
			start(false);
		}
	};

	return (
		<section className="rounded-md border border-secondary p-4">
			<h3 className="text-sm font-semibold text-primary">Backfill history</h3>
			<p className="text-sm text-secondary">
				Verify alert-level measurements that were recorded before verification
				was turned on. Results update past events the same way a new
				verification does: the verifier's decisions in live mode, and the
				footage check's results in any mode while it is on. No notification is
				sent. Events whose severity a customer set are left alone. The worker
				verifies about 180 clips an hour.
			</p>
			<p className="mt-1 text-sm font-medium text-primary">
				With the saved settings: {effect}
			</p>
			<div className="mt-3 flex flex-wrap items-end gap-3">
				<Input
					className="w-auto"
					label="From (inclusive)"
					onChange={setFrom}
					type="date"
					value={from}
				/>
				<Input
					className="w-auto"
					label="To (exclusive)"
					onChange={setTo}
					type="date"
					value={to}
				/>
				<Button
					color="secondary"
					isDisabled={backfill.isPending}
					onClick={() => start(true)}
					size="sm"
					type="button"
				>
					Preview (dry run)
				</Button>
				<Button isDisabled={!canRun} onClick={run} size="sm" type="button">
					{backfill.isPending && totals && !totals.dryRun
						? "Queuing…"
						: "Run backfill"}
				</Button>
			</div>
			{totals && (
				<p className="mt-2 text-sm text-secondary" role="status">
					{totals.dryRun
						? `${totals.candidates} measurements to verify (about $${(
								totals.candidates * COST_PER_CLIP_USD
							).toFixed(2)})`
						: `Queued ${totals.enqueued} of ${totals.candidates} measurements${
								totals.failed > 0 ? `, ${totals.failed} failed` : ""
							}`}{" "}
					across {totals.scannedEvents} events at 5+; {totals.alreadyVerified}{" "}
					already verified
					{totals.skippedEdited > 0 &&
						`; ${totals.skippedEdited} events skipped because a customer set their severity`}
					{totals.tooRecent > 0 &&
						`; ${totals.tooRecent} recorded in the last hour left to the live verifier`}
					.{backfill.isPending && " Still counting…"}
				</p>
			)}
			{totals?.mode === "shadow" && (
				<p className="mt-1 text-sm text-secondary">
					This organization is in shadow mode: results are recorded, but
					measurements and events are left untouched.
				</p>
			)}
			{backfill.error && (
				<p className="mt-2 text-sm text-error-primary" role="alert">
					{backfill.error.message}
				</p>
			)}
		</section>
	);
};
